import { providerFromHost } from './urlProvider.js';

// Sources that name no publisher. `admin` is legacy: manual additions were stamped with it until
// image credits were split from image selection, and nothing writes it any more — but stored refs
// still carry it, and Applauss must never be credited for an image it only references.
const UNATTRIBUTED_SOURCES = new Set(['unknown', 'admin']);

// An image ref's `source` is a publisher credit. Without one, credit whoever the URL belongs
// to — the same inference the API applies (`infer_image_source`), so both sides agree.
//
// Resolve from the parsed host via `providerFromHost`, NOT via `inferUrlProvider`: that one
// carries ticket-editor guards which reject an affiliate-shaped path (`/c/12/34/56/…`, common
// in ordinary CDN URLs) and dotless hosts. The API applies neither to images, so going through
// it credited `unknown` where the API stores the host. Only an absolute http(s) URL names a
// provider, so parse first — a relative '/placeholder.jpg' has no host to read.
export const getImageSource = (url, source) => {
  if (source && !UNATTRIBUTED_SOURCES.has(source)) return source;
  try {
    const parsed = new URL(url);
    return (parsed.protocol.startsWith('http') && providerFromHost(parsed.hostname)) || 'unknown';
  } catch {
    return 'unknown';
  }
};

const positiveDimension = (value) => {
  const dimension = Number(value);
  return Number.isFinite(dimension) && dimension > 0 ? Math.trunc(dimension) : null;
};

export const EVENT_IMAGE_FORMAT_PRIORITY = [
  'RETINA_LANDSCAPE_16_9',
  'TABLET_LANDSCAPE_LARGE_16_9',
  'TABLET_LANDSCAPE_16_9',
  'RECOMENDATION_16_9',
  'RECOMMENDATION_16_9',
  'ARTIST_PAGE_4_3',
  'TABLET_LANDSCAPE_3_2',
];

export const normalizeImageRefs = (images = []) => {
  if (!Array.isArray(images)) return [];
  return images
    .map((image) => {
      const url = typeof image === 'string' ? image : image?.url || image?.src || '';
      const source = typeof image === 'string' ? null : image?.source;
      const width = typeof image === 'string' ? null : positiveDimension(image?.width);
      const height = typeof image === 'string' ? null : positiveDimension(image?.height);
      return {
        url,
        source: getImageSource(url, source),
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
      };
    })
    .filter((image) => image.url);
};

export const selectPreferredImageUrl = (images = []) => {
  const urls = normalizeImageRefs(images).map((image) => image.url);
  for (const format of EVENT_IMAGE_FORMAT_PRIORITY) {
    const match = urls.find((url) => url.includes(format));
    if (match) return match;
  }
  return urls.find((url) => url.includes('16_9')) || urls[0] || null;
};
